import {
  ADD_COMPONENTS,
  ADD_COMPONENT_DOCUMENTS,
  ADD_COMPONENT_LEARNERS,
  EntriesActionTypes,
  RESET_ENTRIES_LIST,
  SET_IS_LOADING_COMPONENTS,
  SET_IS_LOADING_LEARNERS,
  SET_IS_SAVING_RESUBMISSION_STATUS,
  COMPONENT_RESUBMITTION_SUCCESS,
  UPDATE_DOCUMENTS_RESUBMISSION_STATUS,
  UPDATE_LEARNERS_RESUBMISSION_STATUS,
  COMPONENT_RESUBMISSION_STATUS_UPDATED_SUCCESS,
  UPDATE_ASSOCIATED_LEARNERS,
  SET_IS_LINKED_CANDIDATE_MODAL_OPEN,
  SET_SELECTED_LEARNER,
} from './types';
import { RESET_GLOBAL_STATE, GlobalActionTypes } from '../types';
import { unknown } from '../actions';
import { ReplacementFilesState } from '../../types/ReplacementFiles';
import produce from 'immer';
import { ReplacementFilesComponentLearnersDto } from '@cambridgeassessment/lib-ee-commons';

export const initialState: ReplacementFilesState = {
  items: [],
  isLoading: false,
  associatedLearners: [],
  isSaving: false,
  linkedCandidateModal: {
    isShown: false,
    selectedLearner: null,
  },
};

export default (
  state = initialState,
  action: EntriesActionTypes | GlobalActionTypes = unknown(),
): ReplacementFilesState => {
  switch (action.type) {
    case RESET_GLOBAL_STATE:
    case RESET_ENTRIES_LIST: {
      return initialState;
    }

    case ADD_COMPONENTS: {
      const {
        payload: { components },
      } = action;

      const newComponents = components.map((component) => ({
        isAllSelected: false,
        component,
        learners: {
          items: [],
          isLoading: false,
        },
        componentDocuments: {
          items: [],
          isLoading: false,
        },
        associatedLearners: [],
      }));

      return {
        ...state,
        items: [...state.items, ...newComponents],
      };
    }

    case SET_IS_LOADING_COMPONENTS: {
      const {
        payload: { isLoadingComponents },
      } = action;

      return {
        ...state,
        isLoading: isLoadingComponents,
      };
    }

    case UPDATE_DOCUMENTS_RESUBMISSION_STATUS: {
      const {
        payload: { componentIndex, documentId, availableForResubmission },
      } = action;

      const updatedItems = state.items.map((item, index) => {
        if (index !== componentIndex) {
          return item;
        }

        const updatedDocumentItems = item.componentDocuments.items.map((document) => {
          if (document.section.id !== documentId) {
            return document;
          }

          return {
            ...document,
            resubmission: {
              enabled: availableForResubmission,
            },
          };
        });

        return {
          ...item,
          componentDocuments: {
            ...item.componentDocuments,
            items: updatedDocumentItems,
          },
        };
      });

      return {
        ...state,
        items: updatedItems,
      };
    }

    case ADD_COMPONENT_DOCUMENTS: {
      const {
        payload: { componentIndex, documents },
      } = action;

      const updatedItems = state.items.map((item, index) => {
        if (index !== componentIndex) {
          return item;
        }

        return {
          ...item,
          componentDocuments: {
            ...item.componentDocuments,
            items: [...item.componentDocuments.items, ...documents],
          },
        };
      });

      return {
        ...state,
        items: updatedItems,
      };
    }

    case SET_IS_LOADING_LEARNERS: {
      const {
        payload: { componentIndex, isLoadingEntries },
      } = action;

      const updatedItems = state.items.map((item, index) => {
        if (index !== componentIndex) {
          return item;
        }

        return {
          ...item,
          learners: {
            ...item.learners,
            isLoading: isLoadingEntries,
          },
        };
      });

      return {
        ...state,
        items: updatedItems,
      };
    }

    case UPDATE_LEARNERS_RESUBMISSION_STATUS: {
      const {
        payload: { entryIndex, componentIndex, availableForResubmission, associatedLearners },
      } = action;

      associatedLearners.forEach((learner) => {
        const learnerToUpdate = state.items[componentIndex]?.learners.items.find(
          (entry) =>
            entry.businessUnit.id === learner.businessUnitId &&
            entry.assessment.code === learner.assessmentCode &&
            entry.centre.id === learner.centreId &&
            entry.seriesId === learner.seriesId &&
            entry.learner.number === learner.learnerNumber &&
            entry.learner.uniqueId === learner.learnerUniqueId,
        );

        return {
          ...learnerToUpdate,
          resubmission: {
            enabled: availableForResubmission,
          },
        };
      });

      const filteredLearners = associatedLearners.filter((learner) => {
        return (
          learner.learnerNumber !== state.items[componentIndex]?.learners.items[entryIndex]?.learner.number &&
          learner.learnerUniqueId !== state.items[componentIndex]?.learners.items[entryIndex]?.learner.uniqueId
        );
      });

      const newUpdatedItems = [...state.items];
      const updatedItems = newUpdatedItems.map((item, index) => {
        if (index !== componentIndex) {
          return item;
        }

        const updatedLearnerItems = item.learners.items.map((learnerItem, learnerIndex) => {
          if (learnerIndex !== entryIndex) {
            return learnerItem;
          }

          return {
            ...learnerItem,
            resubmission: {
              ...learnerItem.resubmission,
              enabled: availableForResubmission,
            },
          };
        });

        const allUpdatedLearnerItems = updatedLearnerItems.reduce(
          (acc: { [key: string]: ReplacementFilesComponentLearnersDto }, curr) => {
            const key = `${curr.learner.number}-${curr.learner.uniqueId}`;

            const learnerToUpdate = filteredLearners.find(
              (fl) => fl.learnerNumber === curr.learner.number && fl.learnerUniqueId === curr.learner.uniqueId,
            );

            if (learnerToUpdate) {
              acc[key] = {
                ...curr,
                resubmission: {
                  enabled: availableForResubmission,
                },
              };
            } else {
              acc[key] = curr;
            }

            return acc;
          },
          {},
        );

        return {
          ...item,
          learners: {
            ...item.learners,
            items: Object.values(allUpdatedLearnerItems),
          },
        };
      });

      return {
        ...state,
        items: updatedItems,
        associatedLearners: filteredLearners,
      };
    }

    case COMPONENT_RESUBMITTION_SUCCESS: {
      const {
        payload: { componentIndex, availableForResubmission },
      } = action;

      return produce(state, (draft) => {
        const componentToResubmit = draft.items[componentIndex];
        componentToResubmit.isAllSelected = availableForResubmission;
        const resubmittableItems = [
          ...componentToResubmit.componentDocuments.items,
          ...componentToResubmit.learners.items,
        ];
        resubmittableItems.forEach((item) => {
          item.resubmission = { enabled: availableForResubmission };
        });
      });
    }

    case COMPONENT_RESUBMISSION_STATUS_UPDATED_SUCCESS: {
      const { isAllSelected, componentIndex, isAllAbsent } = action.payload;

      return produce(state, (draft) => {
        draft.items[componentIndex].isAllSelected = isAllSelected;
        draft.items[componentIndex].isAllAbsent = isAllAbsent;
      });
    }

    case ADD_COMPONENT_LEARNERS: {
      const {
        payload: { componentIndex, entries, nextUrl },
      } = action;

      const updatedItems = state.items.map((item, index) => {
        if (index !== componentIndex) {
          return item;
        }

        return {
          ...item,
          learners: {
            ...item.learners,
            nextPageUrl: nextUrl,
            items: [...item.learners.items, ...entries],
          },
        };
      });

      return {
        ...state,
        items: updatedItems,
      };
    }

    case SET_IS_SAVING_RESUBMISSION_STATUS: {
      const { isSaving } = action.payload;

      return {
        ...state,
        isSaving,
      };
    }

    case UPDATE_ASSOCIATED_LEARNERS: {
      const { associatedLearners } = action.payload;

      return {
        ...state,
        associatedLearners,
      };
    }

    case SET_IS_LINKED_CANDIDATE_MODAL_OPEN: {
      const { setOpen } = action.payload;

      return {
        ...state,
        linkedCandidateModal: {
          ...state.linkedCandidateModal,
          isShown: setOpen,
        },
      };
    }

    case SET_SELECTED_LEARNER: {
      const { selectedLearner } = action.payload;

      return {
        ...state,
        linkedCandidateModal: {
          ...state.linkedCandidateModal,
          selectedLearner,
        },
      };
    }

    default:
      return state;
  }
};
